import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  Typography,
  Skeleton,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import CachedIcon from "@mui/icons-material/Cached";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import useAD from "../../../hooks/super/useAD";
import useAuth from "../../../hooks/useAuth";

import {
  CollapseAlert,
  LoadingButton,
  SnackbarAlert,
} from "../../../components/pages/common/styled";
import { UserType } from "../../../utils/constants";
import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import PrimaryButton from "../../../components/pages/_new/super/common/PrimaryButton";
import OutlinedButton from "../../../components/pages/_new/super/common/OutlinedButton";
import {
  TOKENS,
  SectionCard,
} from "../../../components/pages/_new/super/design";

function SAAdCfgs() {
  const {
    mitigationTimeout,
    blockUrl,
    detectionConfigs,
    errMsg,
    getMitigationTimeout,
    setMitigationTimeout,
    getBlockUrl,
    setBlockUrl,
    getDetectionConfigs,
    updateDetectionConfig,
    applyAdCfg,
    setErr,
  } = useAD();
  const { isAuthenticated, adminUserType } = useAuth();

  const [snackOpen, setSnackOpen] = React.useState(false);
  const handleSnackClose = () => setSnackOpen(false);
  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const [loading, setLoading] = React.useState(false);

  const adMitigationTimeoutRef = React.useRef(null);
  const [adMitigationTimeoutValue, setAdMitigationTimeoutValue] = React.useState(null);
  const adBlockUrlRef = React.useRef(null);
  const [adBlockUrlValue, setAdBlockUrlValue] = React.useState(null);

  const saveAdMitigationTimeout = async () => {
    if (adMitigationTimeoutValue === null || adMitigationTimeoutValue === undefined) {
      adMitigationTimeoutRef.current?.focus();
      return;
    }
    setLoading(true);
    await setMitigationTimeout(adMitigationTimeoutValue);
    setLoading(false);
  };
  const saveAdBlockUrl = async () => {
    if (adBlockUrlValue === null || adBlockUrlValue === undefined) {
      adBlockUrlRef.current?.focus();
      return;
    }
    setLoading(true);
    await setBlockUrl(adBlockUrlValue);
    setLoading(false);
  };
  const changeAdMitigationTimeout = (e) => setAdMitigationTimeoutValue(e.target.value);
  const changeAdBlockUrl = (e) => setAdBlockUrlValue(e.target.value);

  const refresh = React.useCallback(async () => {
    getMitigationTimeout();
    getBlockUrl();
    getDetectionConfigs();
  }, [getMitigationTimeout, getBlockUrl, getDetectionConfigs]);

  React.useEffect(() => {
    if (isAuthenticated) refresh();
    return () => setErr(null);
  }, [isAuthenticated, setErr, refresh]);

  React.useEffect(() => {
    setAdMitigationTimeoutValue(mitigationTimeout);
  }, [mitigationTimeout]);

  React.useEffect(() => {
    setAdBlockUrlValue(blockUrl);
  }, [blockUrl]);

  const [errOpen, setErrOpen] = React.useState(false);
  React.useEffect(() => {
    if (errMsg) setErrOpen(true);
  }, [errMsg]);

  const apply = async () => {
    if (mitigationTimeout === null || null == blockUrl) {
      setErr("You must configure your settings properly");
      return;
    }
    setLoading(true);
    const result = await applyAdCfg();
    setMessage(result.msg);
    setSuccess(result.status);
    setLoading(false);
    setSnackOpen(true);
  };

  const [editingKey, setEditingKey] = React.useState(null);
  const [editValue, setEditValue] = React.useState("");
  const [savingKey, setSavingKey] = React.useState(null);

  const canEdit = [UserType.SUPER_ADMIN, UserType.ADMIN].includes(adminUserType);

  const startEditing = (key, currentValue) => {
    setEditingKey(key);
    setEditValue(currentValue);
  };
  const cancelEditing = () => {
    setEditingKey(null);
    setEditValue("");
  };
  const saveDetectionValue = async (key) => {
    setSavingKey(key);
    const ok = await updateDetectionConfig(key, editValue);
    if (ok) {
      setMessage(`Updated "${key}" successfully`);
      setSuccess("success");
      setSnackOpen(true);
    }
    setSavingKey(null);
    setEditingKey(null);
    setEditValue("");
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: `${TOKENS.radiusXs}px`,
      background: TOKENS.bgSurface,
    },
  };

  const thCellSx = {
    padding: "10px 14px",
    borderBottom: `1px solid ${TOKENS.border}`,
    background: TOKENS.bgSunken,
    color: TOKENS.fg2,
    fontSize: 11.5,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };
  const tdCellSx = {
    padding: "12px 14px",
    borderBottom: `1px solid ${TOKENS.borderSubtle}`,
    color: TOKENS.fg1,
    fontSize: 13.5,
  };

  return (
    <React.Fragment>
      <Helmet title="SA AD Configuration" />
      <PageHeader
        title="Anti DDoS Configuration"
        description="Configure anti-DDoS protection thresholds and runtime parameters"
        actions={
          <>
            <OutlinedButton
              onClick={refresh}
              startIcon={<CachedIcon style={{ width: 18, height: 18 }} />}
            >
              Refresh
            </OutlinedButton>
            <LoadingButton
              variant="contained"
              startIcon={<SaveIcon style={{ width: 18, height: 18 }} />}
              loadingPosition="start"
              loading={loading}
              onClick={apply}
              disabled={!canEdit}
              sx={{
                height: 36,
                borderRadius: "8px",
                backgroundColor: "#6371F7",
                fontSize: 14,
                fontWeight: 400,
                textTransform: "none",
                "&:hover": { backgroundColor: "#4959ee" },
              }}
            >
              Apply
            </LoadingButton>
          </>
        }
      />
      <Box sx={{ mb: 3 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      <Box sx={{ display: "grid", gap: 2 }}>
        <SectionCard
          title="Mitigation"
          description="How long mitigation actions persist and where blocked clients are redirected"
        >
          <Stack spacing={2.5} sx={{ maxWidth: 720 }}>
            <Box>
              <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2, mb: 0.75, fontWeight: 500 }}>
                Mitigation timeout (seconds)
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
                {!adMitigationTimeoutValue ? (
                  <Skeleton height={40} width="100%" variant="rectangular" sx={{ borderRadius: "8px" }} />
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. 600"
                    required
                    value={adMitigationTimeoutValue}
                    onChange={changeAdMitigationTimeout}
                    disabled={loading}
                    inputRef={adMitigationTimeoutRef}
                    sx={inputSx}
                  />
                )}
                <PrimaryButton
                  onClick={saveAdMitigationTimeout}
                  startIcon={<SaveIcon style={{ width: 18, height: 18 }} />}
                  disabled={loading || !canEdit}
                >
                  Save
                </PrimaryButton>
              </Stack>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2, mb: 0.75, fontWeight: 500 }}>
                DDoS block URL
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
                {!adBlockUrlValue ? (
                  <Skeleton height={40} width="100%" variant="rectangular" sx={{ borderRadius: "8px" }} />
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://..."
                    required
                    value={adBlockUrlValue}
                    onChange={changeAdBlockUrl}
                    inputRef={adBlockUrlRef}
                    disabled={loading}
                    sx={inputSx}
                  />
                )}
                <PrimaryButton
                  onClick={saveAdBlockUrl}
                  startIcon={<SaveIcon style={{ width: 18, height: 18 }} />}
                  disabled={loading || !canEdit}
                >
                  Save
                </PrimaryButton>
              </Stack>
            </Box>
          </Stack>
        </SectionCard>

        <SectionCard
          title="Global detection thresholds"
          description="Per-key tuning knobs for the DDoS detection engine"
        >
          {!detectionConfigs ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={24} sx={{ color: TOKENS.primary }} />
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={thCellSx}>Key</TableCell>
                    <TableCell sx={thCellSx}>Value</TableCell>
                    <TableCell sx={thCellSx}>Strategy</TableCell>
                    <TableCell sx={thCellSx}>Description</TableCell>
                    {canEdit && <TableCell sx={{ ...thCellSx, width: 80 }} align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(Array.isArray(detectionConfigs) ? detectionConfigs : []).map((row) => (
                    <TableRow key={row.key} sx={{ "&:hover": { background: TOKENS.bgSunken } }}>
                      <TableCell
                        sx={{
                          ...tdCellSx,
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                          fontSize: 12,
                          color: TOKENS.fg2,
                        }}
                      >
                        {row.key}
                      </TableCell>
                      <TableCell sx={tdCellSx}>
                        {editingKey === row.key ? (
                          <TextField
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            autoFocus
                            sx={{ width: 120, ...inputSx }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveDetectionValue(row.key);
                              if (e.key === "Escape") cancelEditing();
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 13, fontWeight: 500, color: TOKENS.fg1 }}>
                            {row.value}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...tdCellSx, color: TOKENS.fg2 }}>{row.strategy || "—"}</TableCell>
                      <TableCell sx={{ ...tdCellSx, fontSize: 12, color: TOKENS.fg3 }}>
                        {row.description || "—"}
                      </TableCell>
                      {canEdit && (
                        <TableCell sx={tdCellSx} align="right">
                          {editingKey === row.key ? (
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              {savingKey === row.key ? (
                                <CircularProgress size={16} sx={{ color: TOKENS.primary }} />
                              ) : (
                                <>
                                  <Tooltip title="Save" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => saveDetectionValue(row.key)}
                                      sx={{ color: TOKENS.primary }}
                                      aria-label="Save"
                                    >
                                      <CheckIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Cancel" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={cancelEditing}
                                      sx={{ color: TOKENS.fg2 }}
                                      aria-label="Cancel"
                                    >
                                      <CloseIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Stack>
                          ) : (
                            <Tooltip title="Edit value" arrow>
                              <IconButton
                                size="small"
                                onClick={() => startEditing(row.key, row.value)}
                                sx={{ color: TOKENS.fg2, "&:hover": { color: TOKENS.primary } }}
                                aria-label={`Edit ${row.key}`}
                              >
                                <EditIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      </Box>

      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}
export default SAAdCfgs;
